import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ExpandableSection,
  Box,
  SpaceBetween,
  Badge,
  Button,
  Icon,
  Grid,
  ColumnLayout,
  Container,
  Header,
  StatusIndicator
} from '@cloudscape-design/components';

const HierarchicalTitleView = ({ titles }) => {
  const navigate = useNavigate();
  const [expandedSeries, setExpandedSeries] = useState(new Set());
  const [expandedSeasons, setExpandedSeasons] = useState(new Set());

  // Organize titles into hierarchy
  const organizeHierarchy = (titles) => {
    const hierarchy = {};
    const orphans = [];

    // First pass: create series containers
    titles.forEach(title => {
      if (title.content_type === 'series') {
        hierarchy[title.title_id] = {
          ...title,
          seasons: {}
        };
      }
    });

    // Second pass: add seasons to series
    titles.forEach(title => {
      if (title.content_type === 'season' && title.parent_id) {
        if (hierarchy[title.parent_id]) {
          hierarchy[title.parent_id].seasons[title.title_id] = {
            ...title,
            episodes: []
          };
        } else {
          orphans.push(title);
        }
      }
    });

    // Third pass: add episodes to seasons
    titles.forEach(title => {
      if (title.content_type === 'episode' && title.parent_id) {
        // Find the season this episode belongs to
        Object.values(hierarchy).forEach(series => {
          if (series.seasons[title.parent_id]) {
            series.seasons[title.parent_id].episodes.push(title);
          }
        });
        
        // If no season found, check if it's a direct child of series
        if (!Object.values(hierarchy).some(series => 
          series.seasons[title.parent_id]
        )) {
          if (hierarchy[title.parent_id]) {
            // Direct episode under series (no seasons)
            if (!hierarchy[title.parent_id].directEpisodes) {
              hierarchy[title.parent_id].directEpisodes = [];
            }
            hierarchy[title.parent_id].directEpisodes.push(title);
          } else {
            orphans.push(title);
          }
        }
      }
    });

    // Add standalone content (films, documentaries, etc.)
    titles.forEach(title => {
      if (!['series', 'season', 'episode'].includes(title.content_type)) {
        orphans.push(title);
      }
    });

    return { hierarchy, orphans };
  };

  const { hierarchy, orphans } = organizeHierarchy(titles);

  const toggleSeries = (seriesId) => {
    const newExpanded = new Set(expandedSeries);
    if (newExpanded.has(seriesId)) {
      newExpanded.delete(seriesId);
    } else {
      newExpanded.add(seriesId);
    }
    setExpandedSeries(newExpanded);
  };

  const toggleSeason = (seasonId) => {
    const newExpanded = new Set(expandedSeasons);
    if (newExpanded.has(seasonId)) {
      newExpanded.delete(seasonId);
    } else {
      newExpanded.add(seasonId);
    }
    setExpandedSeasons(newExpanded);
  };

  const renderPoster = (item) => (
    <Box>
      {item.metadata?.poster_url ? (
        <img 
          src={item.metadata.poster_url} 
          alt={item.metadata.title}
          style={{ 
            width: '60px', 
            height: '90px', 
            objectFit: 'cover',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        />
      ) : (
        <Box 
          textAlign="center" 
          color="text-status-inactive"
          style={{
            width: '60px',
            height: '90px',
            border: '2px dashed #ccc',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon name="image" />
        </Box>
      )}
    </Box>
  );

  const renderTitleInfo = (item) => (
    <ColumnLayout columns={4} variant="text-grid">
      <div>
        <Box variant="awsui-key-label">Title</Box>
        <Button 
          variant="link" 
          onClick={() => navigate(`/titles/${item.title_id}`)}
        >
          {item.metadata?.title || item.title_id}
        </Button>
      </div>
      <div>
        <Box variant="awsui-key-label">Year</Box>
        <Box>{item.metadata?.year || '-'}</Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Genre</Box>
        <Box>{item.metadata?.genre?.join(', ') || '-'}</Box>
      </div>
      <div>
        <Box variant="awsui-key-label">Rating</Box>
        <Box>{item.metadata?.imdb_rating ? `⭐ ${item.metadata.imdb_rating}` : '-'}</Box>
      </div>
    </ColumnLayout>
  );

  const renderEpisode = (episode) => (
    <Container key={episode.title_id}>
      <Grid gridDefinition={[{ colspan: 2 }, { colspan: 10 }]}>
        <div>{renderPoster(episode)}</div>
        <div>
          <SpaceBetween size="xs">
            <Box>
              <Badge color="green">Episode {episode.metadata?.episode_number || '?'}</Badge>
              <Box variant="h4" display="inline" margin={{ left: 's' }}>
                <Button 
                  variant="link" 
                  onClick={() => navigate(`/titles/${episode.title_id}`)}
                >
                  {episode.metadata?.title || episode.title_id}
                </Button>
              </Box>
            </Box>
            {episode.metadata?.synopsis && (
              <Box variant="p" color="text-body-secondary">
                {episode.metadata.synopsis.substring(0, 200)}
                {episode.metadata.synopsis.length > 200 && '...'}
              </Box>
            )}
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Duration</Box>
                <Box>{episode.metadata?.duration_minutes ? `${episode.metadata.duration_minutes} min` : '-'}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label">Status</Box>
                <Badge color="blue">{episode.metadata?.status || 'draft'}</Badge>
              </div>
              <div>
                <Box variant="awsui-key-label">Rating</Box>
                <Box>{episode.metadata?.imdb_rating ? `⭐ ${episode.metadata.imdb_rating}` : '-'}</Box>
              </div>
            </ColumnLayout>
          </SpaceBetween>
        </div>
      </Grid>
    </Container>
  );

  const renderSeason = (season, seriesId) => {
    const seasonEpisodes = [...season.episodes].sort((a, b) => 
      (a.metadata?.episode_number || 0) - (b.metadata?.episode_number || 0)
    );

    return (
      <Container key={season.title_id}>
        <ExpandableSection
          expanded={expandedSeasons.has(season.title_id)}
          onChange={() => toggleSeason(season.title_id)}
          headerText={
            <Box>
              <Badge color="orange">Season {season.metadata?.season_number || '?'}</Badge>
              <Box display="inline" margin={{ left: 's' }}>
                {season.metadata?.title || `Season ${season.metadata?.season_number || '?'}`}
              </Box>
              <Box display="inline" margin={{ left: 's' }} color="text-body-secondary">
                ({seasonEpisodes.length} episodes)
              </Box>
            </Box>
          }
        >
          <Grid gridDefinition={[{ colspan: 2 }, { colspan: 10 }]}>
            <div>{renderPoster(season)}</div>
            <div>
              <SpaceBetween size="m">
                {renderTitleInfo(season)}
                {season.metadata?.synopsis && (
                  <Box variant="p">
                    {season.metadata.synopsis}
                  </Box>
                )}
                <SpaceBetween size="s">
                  <Box variant="h5">Episodes ({seasonEpisodes.length})</Box>
                  {seasonEpisodes.map(episode => renderEpisode(episode))}
                </SpaceBetween>
              </SpaceBetween>
            </div>
          </Grid>
        </ExpandableSection>
      </Container>
    );
  };

  const renderSeries = (series) => {
    const seasons = Object.values(series.seasons).sort((a, b) => 
      (a.metadata?.season_number || 0) - (b.metadata?.season_number || 0)
    );
    const directEpisodes = series.directEpisodes || [];
    const totalEpisodes = seasons.reduce((acc, season) => acc + season.episodes.length, 0) + directEpisodes.length;

    return (
      <Container key={series.title_id} header={
        <Header
          variant="h2"
          actions={
            <Button 
              variant="primary" 
              onClick={() => navigate(`/titles/${series.title_id}`)}
            >
              View Details
            </Button>
          }
        >
          <Badge color="red">Series</Badge>
          <Box display="inline" margin={{ left: 's' }}>
            {series.metadata?.title || series.title_id}
          </Box>
          <Box display="inline" margin={{ left: 's' }} color="text-body-secondary">
            ({seasons.length} seasons, {totalEpisodes} episodes)
          </Box>
        </Header>
      }>
        <ExpandableSection
          expanded={expandedSeries.has(series.title_id)}
          onChange={() => toggleSeries(series.title_id)}
          headerText="Show seasons and episodes"
        >
          <Grid gridDefinition={[{ colspan: 2 }, { colspan: 10 }]}>
            <div>{renderPoster(series)}</div>
            <div>
              <SpaceBetween size="l">
                {renderTitleInfo(series)}
                {series.metadata?.synopsis && (
                  <Box variant="p">
                    {series.metadata.synopsis}
                  </Box>
                )}
                
                {seasons.length > 0 && (
                  <SpaceBetween size="m">
                    <Box variant="h4">Seasons ({seasons.length})</Box>
                    {seasons.map(season => renderSeason(season, series.title_id))}
                  </SpaceBetween>
                )}
                
                {directEpisodes.length > 0 && (
                  <SpaceBetween size="m">
                    <Box variant="h4">Episodes ({directEpisodes.length})</Box>
                    {directEpisodes.map(episode => renderEpisode(episode))}
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </div>
          </Grid>
        </ExpandableSection>
      </Container>
    );
  };

  const renderStandaloneContent = (item) => (
    <Container key={item.title_id}>
      <Grid gridDefinition={[{ colspan: 2 }, { colspan: 10 }]}>
        <div>{renderPoster(item)}</div>
        <div>
          <SpaceBetween size="s">
            <Box>
              <Badge>{item.content_type}</Badge>
              <Box variant="h4" display="inline" margin={{ left: 's' }}>
                <Button 
                  variant="link" 
                  onClick={() => navigate(`/titles/${item.title_id}`)}
                >
                  {item.metadata?.title || item.title_id}
                </Button>
              </Box>
            </Box>
            {renderTitleInfo(item)}
            {item.metadata?.synopsis && (
              <Box variant="p">
                {item.metadata.synopsis.substring(0, 300)}
                {item.metadata.synopsis.length > 300 && '...'}
              </Box>
            )}
          </SpaceBetween>
        </div>
      </Grid>
    </Container>
  );

  return (
    <SpaceBetween size="l">
      {/* Series with hierarchical view */}
      {Object.values(hierarchy).map(series => renderSeries(series))}
      
      {/* Standalone content (films, documentaries, etc.) */}
      {orphans.length > 0 && (
        <Container header={<Header variant="h2">Other Content ({orphans.length})</Header>}>
          <SpaceBetween size="m">
            {orphans.map(item => renderStandaloneContent(item))}
          </SpaceBetween>
        </Container>
      )}
      
      {Object.keys(hierarchy).length === 0 && orphans.length === 0 && (
        <Box textAlign="center" color="inherit">
          <b>No titles</b>
          <Box padding={{ bottom: "s" }} variant="p" color="inherit">
            No titles to display.
          </Box>
        </Box>
      )}
    </SpaceBetween>
  );
};

export default HierarchicalTitleView;